import { Link } from "react-router-dom";
import moment from "moment";
import { DataTableSkeleton } from "@carbon/react";
import { DataTableSkeletonHeader } from "@carbon/react/lib/components/DataTableSkeleton/DataTableSkeleton";

import {
  getPaymentStatusIndicator,
  truncateStringWithEllipsis,
} from "../../utils";
import {
  Badge,
  CopyButton,
  CurrencyDisplay,
  DataLoadingView,
  NoDataToSeeContainer,
  StructuredList,
} from "..";
import { appConfig } from "../../configs";
import { IPaymentsListViewProps } from "./PaymentsList";

const headers: any /*DataTableSkeletonHeader*/[] = [
  {
    key: "xxxxxxx",
    header: "AMOUNT",
  },
  {
    key: "xxxxxxx",
    header: "PAID",
  },
  {
    key: "xxxxxxx",
    header: "STATUS",
  },
  {
    key: "xxxxxxx",
    header: "REFERENCE",
  },
  {
    key: "xxxxxxx",
    header: "PROVIDER",
  },
  {
    key: "xxxxxxx",
    header: "DATE CREATED",
  },
  {
    key: "xxxxxxx",
    header: "PAID ON",
  },
];

export const View = (props: IPaymentsListViewProps) => {
  const { isLoading, isError, data, loadingMessage, filterList } = props;
  const transactionsList = filterList
    ? data?.filter((tx) => filterList(tx))
    : data;

  if (isLoading)
    return (
      <DataTableSkeleton
        columnCount={headers.length}
        rowCount={5}
        headers={headers}
        showHeader={false}
        showToolbar={false}
      />
    );

  return (
    <StructuredList
      headers={
        <div className="cds--structured-list-row cds--structured-list-row--header-row">
          <div className="cds--structured-list-th pr-5 align-text--center">
            AMOUNT
          </div>
          <div className="cds--structured-list-th pr-5 align-text--center">
            PAID
          </div>
          <div className="cds--structured-list-th">STATUS</div>
          <div className="cds--structured-list-th">REFERENCE</div>
          <div className="cds--structured-list-th">PROVIDER &nbsp;</div>
          <div className="cds--structured-list-th">DATE CREATED</div>
          <div className="cds--structured-list-th">PAID ON</div>
        </div>
      }
      {...{
        rows: isLoading ? (
          <div className="cds--structured-list-row">
            <div className="flex-container col justify-content--center align-items--center">
              <DataLoadingView
                message={loadingMessage || "Loading transactions..."}
              />
            </div>
          </div>
        ) : isError ? (
          <div>An error occured. Could not retrieve data.</div>
        ) : !transactionsList || transactionsList.length === 0 ? (
          <NoDataToSeeContainer errorMessage="No related payments in the last 6 months" />
        ) : (
          transactionsList.map((transaction) => {
            const statusIndicator = getPaymentStatusIndicator(transaction);

            return (
              <Link
                to={`/payments/${transaction.id}/details`}
                state={{
                  transactionToViewDetails: transaction,
                }}
                className="cds--structured-list-tbody"
              >
                <div className="cds--structured-list-row">
                  <div className="cds--structured-list-td align-text--right">
                    <CurrencyDisplay
                      amount={transaction.amount}
                      currencyOrSymbol={transaction.currency}
                      amountFontSize={16}
                      symbolFontSize={12}
                      amountClassName={""}
                      symbolClassName={""}
                    />
                  </div>
                  <div className="cds--structured-list-td align-text--right">
                    <CurrencyDisplay
                      amount={transaction.amount_paid}
                      currencyOrSymbol={transaction.currency}
                      amountFontSize={16}
                      symbolFontSize={12}
                      amountClassName={""}
                      symbolClassName={""}
                    />
                  </div>
                  <div className="cds--structured-list-td">
                    <Badge
                      text={statusIndicator.statusText}
                      statusColor={statusIndicator.statusType}
                      hideIndicator
                      className=""
                    />
                  </div>
                  <div
                    className="cds--structured-list-td"
                    title={transaction.tx_ref}
                  >
                    <div className="flex-container justify-content--start align-items--center no-wrap">
                      <div className="transaction-id-text modal-header--title">
                        <span title={transaction.tx_ref}>
                          {truncateStringWithEllipsis(transaction.tx_ref, 15)}
                        </span>
                      </div>
                      <div className="copy-button--container ml-3">
                        <CopyButton
                          name="copy-reference-button"
                          stringToCopy={transaction.tx_ref}
                          iconDescription="copy reference"
                          className="copy-btn"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className="cds--structured-list-td"
                    title={transaction.gateway_code}
                  >
                    <span className=" gateway-display">
                      {!transaction.gateway_code ? (
                        "-"
                      ) : (
                        <div className="flex-container pr-5 justify-content--center">
                          <img
                            alt="gateway"
                            src={`/gateway-logos/${transaction.gateway_code}-icon.svg`}
                            className="gateway-icon--small"
                            title={transaction.gateway_code}
                          />
                        </div>
                      )}
                    </span>
                  </div>
                  <div className="cds--structured-list-td">
                    {moment(transaction.created_at).format(
                      appConfig.dateTimeFormat
                    )}
                  </div>
                  <div className="cds--structured-list-td">
                    {!transaction.paid_at
                      ? "--"
                      : moment(transaction.paid_at).format(
                          appConfig.dateTimeFormat
                        )}
                  </div>
                </div>
              </Link>
            );
          })
        ),
      }}
    />
  );
};
